import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Group,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import {
  Alert,
  Button,
  LoadingScreen,
  SectionCard,
  spacing,
} from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type {
  ReviewDecisionRequest,
  ReviewDecisionResponse,
  ReviewDecisionVerb,
  ReviewImageDetail,
  ReviewImageSummary,
} from "@models/review";
import type { Q1Score } from "@models/q1";
import { Q1Panel, type Q1Values } from "./Q1Panel";
import { DecisionCard } from "./DecisionCard";
import { ImageTile } from "./ImageTile";
import type { ReviewSubStep } from "./ReviewSidebar";
import { errorMessageKey } from "@utils/errorMessages";
import "./ReviewMain.scss";

interface ReviewMainProps {
  /** The active image summary (gives #n / objId / origin). */
  image: ReviewImageSummary;
  /** 1-based position of the image in the run (for the `#n` label). */
  position: number;
  /** The loaded detail, or null while loading. */
  detail: ReviewImageDetail | null;
  /** i18n key for a load error, or null. */
  loadErrorKey: string | null;
  activeSub: ReviewSubStep;
  onSelectSub: (sub: ReviewSubStep) => void;
  /** Report the local Q1/decision answered state up to the sidebar. */
  onQ1AnsweredChange: (answered: boolean) => void;
  /** Called after a successful decision with the new state. */
  onDecided: (state: "approved" | "rejected") => void;
  /** Report the decision save status up to the workspace toolbar. */
  onSaveStateChange: (state: "idle" | "saving" | "saved" | "error") => void;
  /** Decide one image. */
  decide: (body: ReviewDecisionRequest) => Promise<ReviewDecisionResponse>;
  /** Advance to the next pending image (or null if none left). */
  onNext: () => void;
  /** Whether there is a next pending image to advance to. */
  hasNext: boolean;
}

/**
 * The centered main column (max ~1024px). Title row with origin + gate badges
 * and the Q1 / Decision step buttons; a two-column grid of square Source /
 * Redraw tiles; the scrollable context panel; the Q1 panel OR the Decision
 * card (switched by the active sub-step); and a footer row with a top border —
 * status text on the left, a Next step / Next pending button on the right.
 * Mirrors the reviewer-app `ReviewerView`'s main pane, rebuilt on the design
 * system.
 */
export function ReviewMain({
  image,
  position,
  detail,
  loadErrorKey,
  onSelectSub,
  onQ1AnsweredChange,
  onDecided,
  onSaveStateChange,
  decide,
  onNext,
  hasNext,
}: ReviewMainProps) {
  const { t } = useTranslation();

  const [q1, setQ1] = useState<Q1Values>({
    score: null,
    distinctness: null,
    accuracy: null,
  });
  const [reason, setReason] = useState("");
  const [validationKey, setValidationKey] = useState<string | null>(null);
  const [decideErrorKey, setDecideErrorKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<ReviewDecisionVerb | null>(null);

  const alreadyDecided =
    image.reviewState === "approved" || image.reviewState === "rejected"
      ? image.reviewState
      : null;

  // Reset the local decision state whenever the active image changes.
  useEffect(() => {
    setQ1({
      score: null,
      distinctness: detail?.distinctness ?? null,
      accuracy: detail?.accuracy ?? null,
    });
    setReason("");
    setValidationKey(null);
    setDecideErrorKey(null);
  }, [image.requestId, image.objId, detail?.distinctness, detail?.accuracy]);

  // Tell the sidebar whether Q1 has an answer, for its sub-step check.
  useEffect(() => {
    onQ1AnsweredChange(q1.score != null);
  }, [q1.score, onQ1AnsweredChange]);

  const hasModelValues = useMemo(
    () => detail?.distinctness != null || detail?.accuracy != null,
    [detail],
  );

  const submit = useCallback(
    async (decision: ReviewDecisionVerb) => {
      setValidationKey(null);
      setDecideErrorKey(null);

      // Q1 score is required on BOTH decisions (D2).
      if (q1.score == null) {
        setValidationKey("review.decide.scoreRequired");
        onSelectSub("q1");
        return;
      }
      // Reject also requires a reason (D2).
      if (decision === "reject" && reason.trim().length === 0) {
        setValidationKey("review.decide.reasonRequired");
        return;
      }

      const body: ReviewDecisionRequest = {
        decision,
        score: q1.score as Q1Score,
      };
      if (decision === "reject") body.reason = reason.trim();
      // Sliders are sent ONLY when the image carried a model value (I-11).
      if (hasModelValues) {
        if (q1.distinctness != null) body.distinctness = q1.distinctness;
        if (q1.accuracy != null) body.accuracy = q1.accuracy;
      }

      setSubmitting(decision);
      onSaveStateChange("saving");
      try {
        const res = await decide(body);
        onSaveStateChange("saved");
        onDecided(res.reviewState as "approved" | "rejected");
      } catch (error) {
        onSaveStateChange("error");
        setDecideErrorKey(errorMessageKey(error));
      } finally {
        setSubmitting(null);
      }
    },
    [q1, reason, hasModelValues, decide, onDecided, onSaveStateChange, onSelectSub],
  );

  if (loadErrorKey) {
    return (
      <Box className="review-main" p={spacing.xl}>
        <Alert color="rapidRed" title={t("review.errors.title")}>
          {t(loadErrorKey)}
        </Alert>
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box className="review-main">
        <LoadingScreen message={t("common.loading")} />
      </Box>
    );
  }

  const title = t("review.main.imageTitle", { n: position, objId: image.objId });
  const decided = alreadyDecided;
  const statusText = decided
    ? t("review.main.statusDecided")
    : q1.score == null
      ? t("review.main.statusNeedScore")
      : t("review.main.statusReady");

  return (
    <ScrollArea className="review-main" type="auto">
      <Stack className="review-main__column" gap={spacing.lg} p={spacing.lg}>
        <Stack gap={spacing.xs}>
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text fw={600} fz={18} className="review-main__title">
              {title}
            </Text>
          </Group>

          {/* Quiet single meta line under the title — no coloured badges in the
              title row (parity with the old app): small muted text with a small
              coloured dot for the gate result. */}
          <Group gap={spacing.sm} align="center" wrap="nowrap">
            {image.origin && (
              <Text size="xs" c="dimmed">
                {t(`review.images.origin.${image.origin}`, {
                  defaultValue: image.origin,
                })}
              </Text>
            )}
            {image.origin && (
              <Text size="xs" c="dimmed" aria-hidden="true">
                ·
              </Text>
            )}
            <Group gap={4} align="center" wrap="nowrap">
              <span
                className={`review-main__dot review-main__dot--${
                  detail.gatesDegraded ? "warning" : "ok"
                }`}
                aria-hidden="true"
              />
              <Text size="xs" c="dimmed">
                {detail.gatesDegraded
                  ? t("review.detail.gatesDegraded")
                  : t("review.detail.gatesOk")}
              </Text>
            </Group>
          </Group>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={spacing.md}>
          <ImageTile
            url={detail.sourceUrl}
            alt={t("review.detail.sourceAlt")}
            label={t("review.detail.source")}
            downloadName={`${image.objId}-source.png`}
          />
          <ImageTile
            url={detail.finalUrl}
            alt={t("review.detail.redrawAlt")}
            label={t("review.detail.redraw")}
            downloadName={`${image.objId}-redraw.png`}
          />
        </SimpleGrid>

        <SectionCard title={t("review.detail.contextTitle")}>
          <ScrollArea.Autosize mah={200}>
            <Text size="sm" className="review-main__context">
              {detail.context ?? t("review.detail.noContext")}
            </Text>
          </ScrollArea.Autosize>
        </SectionCard>

        {decided ? (
          <Alert
            color={decided === "approved" ? "green" : "rapidRed"}
            title={t("review.decide.doneTitle")}
          >
            {t(`review.decide.done.${decided}`)}
          </Alert>
        ) : (
          <DecisionCard
            reason={reason}
            onReasonChange={setReason}
            validationKey={validationKey}
            errorKey={decideErrorKey}
            submitting={submitting}
            onDecide={submit}
          >
            <Q1Panel value={q1} onChange={setQ1} hasModelValues={hasModelValues} />
          </DecisionCard>
        )}

        <Group className="review-main__footer" justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed">
            {statusText}
          </Text>
          <Group gap={spacing.sm} wrap="nowrap">
            {decided && (
              <Button
                rightSection={<IconArrowRight size={16} />}
                onClick={onNext}
                disabled={!hasNext}
              >
                {t("review.main.nextPending")}
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </ScrollArea>
  );
}
